<template>
    <div>
        <h1>Rent-To-Own Pricing Configuration</h1>
        <p v-show="thinking">Loading...</p>

        <!-- Coefficient Configuration -->
        <div v-if="!thinking" class="config-section">
            <p>Adjust the coefficients used in the rent-to-own pricing formula for PDF Quotes. Changes will affect all future quotes.This formula is a general representation of the pricing model from Kim's excel spreadsheet</p>
            <code>fee = [(pct_locker_price × locker_price) + (pct_service_fee × service_fee) + (pct_platinum_fee × platinum_fee)</code><br />
            <code>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;+ (mult_num_doors × num_doors) + (mult_num_apartments × num_apartments)  + (additional_cost)] ÷ periods_per_year</code>
            <p><br /></p>
            <table>
                <tr>
                    <th>Parameter</th>
                    <th>Label</th>
                    <th>Value</th>
                </tr>
                <tr>
                    <td>pct_locker_price</td>
                    <td>Percentage Locker Price</td>
                    <td><input type="number" step="0.01" min="0" v-model.number="coefficients.rto_pct_locker_price" /> %</td>
                </tr>
                <tr>
                    <td>pct_service_fee</td>
                    <td>Percentage Yearly Service Fee</td>
                    <td><input type="number" step="0.01" min="0" v-model.number="coefficients.rto_pct_service_fee" /> %</td>
                </tr>
                <tr>
                    <td>pct_platinum_fee</td>
                    <td>Percentage Platinum Support Fee</td>
                    <td><input type="number" step="0.01" min="0" v-model.number="coefficients.rto_pct_platinum_fee" /> %</td>
                </tr>
                <tr>
                    <td>mult_num_doors</td>
                    <td>Number of Locker Doors Multiplier</td>
                    <td><input type="number" step="0.01" min="0" v-model.number="coefficients.rto_mult_num_doors" /></td>
                </tr>
                <tr>
                    <td>mult_num_apartments</td>
                    <td>Number of Apartments Multiplier</td>
                    <td><input type="number" step="0.01" min="0" v-model.number="coefficients.rto_mult_num_apartments" /></td>
                </tr>
                <tr>
                    <td>additional_cost</td>
                    <td>Additional Cost</td>
                    <td><input type="number" step="0.01" min="0" v-model.number="coefficients.rto_additional_cost" /> $</td>
                </tr>
                <tr>
                    <td>periods_per_year</td>
                    <td>Periods per year</td>
                    <td><input type="number" step="0.01" min="0.01" v-model.number="coefficients.rto_periods_per_year" /></td>
                </tr>
            </table>
            <div class="button-row">
                <button @click="saveCoefficients()">Save Coefficients</button>
                <span v-if="hasUnsavedChanges" class="unsaved-warning">⚠ Unsaved Changes</span>
            </div>
        </div>

        <!-- Preview Section -->
        <div v-if="!thinking" class="preview-section">
            <h2>Preview</h2>
            <div class="preview-selectors">
                <label>
                    Project:
                    <SearchableDropdown
                        v-model="preview.selectedProject"
                        :items="projectsForDropdown"
                        item-key="id"
                        item-label="displayName"
                        placeholder="Search projects..."
                        input-id="rto-project-search"
                        @change="updatePreview"
                    />
                </label>

                <label>
                    Locker:
                    <SearchableDropdown
                        v-model="preview.selectedLocker"
                        :items="lockersForDropdown"
                        item-key="id"
                        item-label="displayName"
                        placeholder="Search lockers..."
                        input-id="rto-locker-search"
                        @change="updatePreview"
                    />
                </label>
            </div>

            <div v-if="previewData" class="formula-preview">
                <div class="formula-columns">
                    <div class="formula-box formula-aligned">
                        <code>weekly_fee = (pct_locker_price    × locker_price</code>
                        <code>           + pct_service_fee     × service_fee</code>
                        <code>           + pct_platinum_fee    × platinum_fee</code>
                        <code>           + mult_num_doors      × num_doors</code>
                        <code>           + mult_num_apartments × num_apartments</code>
                        <code>           + additional_cost)    ÷ periods_per_year</code>
                    </div>
                    <div class="formula-box formula-aligned">
                        <code>weekly_fee = (${{ previewData.locker_price }} × {{ coefficients.rto_pct_locker_price }}%</code>
                        <code>           + {{ coefficients.rto_pct_service_fee }}% × ${{ previewData.service_fee }} </code>
                        <code>           + {{ coefficients.rto_pct_platinum_fee }}% × ${{ previewData.platinum_fee }} </code>
                        <code>           + {{ coefficients.rto_mult_num_doors }} × {{ previewData.num_doors }} </code>
                        <code>           + {{ coefficients.rto_mult_num_apartments }} × {{ previewData.num_apartments }} </code>
                        <code>           + ${{coefficients.rto_additional_cost}}) ÷ {{ coefficients.rto_periods_per_year }}</code>
                    </div>
                </div>

                <div class="result-box">
                    <h2>${{ calculatedWeeklyFee }} per week</h2>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { CallApi } from '../Utilities.js'
import SearchableDropdown from '../components/SearchableDropdown.vue'

export default {
    components: {
        SearchableDropdown
    },
    data: function () {
        return {
            thinking: true,
            coefficients: {
                rto_pct_locker_price: 23.04,
                rto_pct_service_fee: 0,
                rto_pct_platinum_fee: 1.0,
                rto_mult_num_doors: 0,
                rto_mult_num_apartments: 0,
                rto_additional_cost: 0,
                rto_periods_per_year: 52
            },
            savedCoefficients: null,
            projects: [],
            lockers: [],
            serviceFees: [],
            preview: {
                selectedProject: null,
                selectedLocker: null
            },
            previewData: null
        }
    },

    computed: {
        calculatedWeeklyFee: function () {
            if (!this.previewData) return '0.00'
            const c = this.coefficients
            const d = this.previewData
            const fee = ((c.rto_pct_locker_price / 100) * d.locker_price + (c.rto_pct_service_fee / 100) * d.service_fee + (c.rto_pct_platinum_fee / 100) * d.platinum_fee + c.rto_mult_num_doors * d.num_doors + c.rto_mult_num_apartments * d.num_apartments + c.rto_additional_cost) / c.rto_periods_per_year
            return fee.toFixed(2)
        },
        hasUnsavedChanges: function () {
            if (!this.savedCoefficients) return false
            return JSON.stringify(this.coefficients) !== JSON.stringify(this.savedCoefficients)
        },
        projectsForDropdown: function () {
            return this.projects.map(p => ({
                id: p.id,
                displayName: `${p.name} (${p.num_apartments} apts)`,
                name: p.name,
                num_apartments: p.num_apartments
            }))
        },
        lockersForDropdown: function () {
            return this.lockers.map(l => ({
                id: l.id,
                displayName: `${l.name} (${l.totalLockers} doors)`,
                name: l.name,
                totalLockers: l.totalLockers,
                totalPrice: l.totalPrice
            }))
        }
    },

    methods: {
        loadCoefficients: async function () {
            this.thinking = true
            const apiResult = await CallApi('GET', '/renttoown')
            if (apiResult && apiResult.coefficients) {
                this.coefficients = apiResult.coefficients
                this.savedCoefficients = JSON.parse(JSON.stringify(apiResult.coefficients))
            }
            this.thinking = false
        },

        loadProjectsAndLockers: async function () {
            const [projectsResult, lockersResult, feesResult] = await Promise.all([
                CallApi('GET', '/projects'),
                CallApi('GET', '/combinations'),
                CallApi('GET', '/fees')
            ])
            this.projects = projectsResult.projects || []
            this.lockers = lockersResult.combinations || []
            this.serviceFees = feesResult.service_fees || []
        },

        saveCoefficients: async function () {
            if (!window.confirm('Update rent-to-own pricing coefficients? This will affect all future quotes.')) return
            this.thinking = true
            await CallApi('POST', '/renttoown/update', { coefficients: this.coefficients })
            await this.loadCoefficients()
            this.thinking = false
        },

        updatePreview: function () {
            if (!this.preview.selectedProject || !this.preview.selectedLocker) {
                this.previewData = null
                return
            }
            const project = this.projects.find(p => p.id === this.preview.selectedProject)
            const locker = this.lockers.find(l => l.id === this.preview.selectedLocker)
            if (!project || !locker) {
                this.previewData = null
                return
            }
            const applicableFee = this.serviceFees.find(fee => {
                return project.num_apartments >= fee.min_apartments && project.num_apartments <= fee.max_apartments && locker.totalLockers >= fee.min_doors && locker.totalLockers <= fee.max_doors
            })
            if (!applicableFee) {
                alert('No applicable service fee found for this combination')
                this.previewData = null
                return
            }
            this.previewData = {
                locker_price: parseFloat(locker.totalPrice),
                service_fee: parseFloat(applicableFee.service_fee),
                platinum_fee: parseFloat(applicableFee.fee_platinum),
                num_doors: parseInt(locker.totalLockers),
                num_apartments: parseInt(project.num_apartments)
            }
        }
    },

    mounted: async function () {
        await this.loadCoefficients()
        await this.loadProjectsAndLockers()
    }
}
</script>

<style scoped>
.config-section, .preview-section {
    margin-bottom: 2rem;
}

table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1rem;
}

th, td {
    padding: 0rem;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

th {
    background: #f5f5f5;
    font-weight: bold;
}

input[type="number"] {
    width: 80px;
    padding: 0.25rem;
}

.button-row {
    display: flex;
    align-items: baseline;
    gap: 1rem;
    margin-top: 1rem;
}

.unsaved-warning {
    color: #e34a0d;
    font-weight: bold;
}

.preview-selectors {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
}

.preview-selectors label {
    display: block;
    font-weight: bold;
}

.preview-selectors select {
    width: 100%;
    padding: 0.5rem;
    margin-top: 0.25rem;
}

.formula-preview h3 {
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
}

.formula-columns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
}

.formula-box {
    background: #f5f5f5;
    padding: 1rem;
    border-radius: 4px;
}

.formula-box code {
    display: block;
    overflow-x: auto;
    font-family: 'Courier New', Courier, monospace;
    margin-bottom: 0;
    padding-bottom: 0;
    white-space: pre;
}

.result-box {
    background: #e8f5e9;
    padding: 1.5rem;
    border-radius: 4px;
    text-align: center;
    margin-top: 1.5rem;
}

.result-box h2 {
    margin: 0;
    color: #2e7d32;
}
</style>
